import {
    OwnerKindEnum,
    PayrollDocument,
    PayrollDocumentKindEnum,
    PayrollDocumentLibrary,
    PayrollDocumentUnavailableReasonEnum,
    PayrollLibraryDocument,
    PayrollSignatureQueue,
    PayrollSignatureQueueItem,
} from 'services/payroll/api/generated/v1/model';

/**
 * One row per shape the acceptance criteria calls out by name: a company filing, an employee W-2, a
 * contractor 1099, a draft, an outstanding signature, a signed one, and — the one the
 * no-client-filtering rule exists for — a `kind: 'other'` row with a `document_type` this app has
 * never heard of. Deleting any one of these should make a specific story or test fail, not just
 * shrink a count.
 *
 * It also covers all six `kind` values, which is asserted rather than left to inspection: three of
 * `DocumentKindChip`'s colours were rendering nowhere but that component's own stories. The slugs
 * are the ones the spec names (`w2`, `1099_nec`, `form_941`, `w4_federal`, `i9`, `w9`, `pay_stub`,
 * `tax_notice`), which are ours rather than the provider's — Gusto sends `US_W-2` for a W-2 and
 * `company_direct_deposit` for a deposit authorisation, and the adapter that maps between them does
 * not exist yet.
 *
 * `worker_uuid: 'employee-1'` / `'contractor-1'` match `usePayrollTeam.mocks.ts`'s
 * `MOCK_EMPLOYEE_DETAIL`/`MOCK_CONTRACTOR_DETAIL` ids on purpose — the Team detail takeover's
 * Documents tab stories/demo filter this fixture by worker, and need at least one real match.
 */
export const MOCK_DOCUMENTS: PayrollDocument[] = [
    {
        created_at: '2026-07-15T12:00:00Z',
        display_name: 'Form 941 — Q2 2026',
        document_type: 'form_941',
        document_uuid: 'document-company-941',
        is_draft: false,
        kind: PayrollDocumentKindEnum.tax_form,
        owner_kind: OwnerKindEnum.company,
        requires_signature: false,
        signed_at: null,
        tax_quarter: 2,
        tax_year: 2026,
        worker_name: '',
        worker_uuid: null,
        category: 'form',
    },
    {
        created_at: '2026-01-31T09:00:00Z',
        display_name: 'W-2 — 2025',
        document_type: 'w2',
        document_uuid: 'document-employee-w2',
        is_draft: false,
        kind: PayrollDocumentKindEnum.tax_form,
        owner_kind: OwnerKindEnum.employee,
        requires_signature: false,
        signed_at: null,
        tax_quarter: null,
        tax_year: 2025,
        worker_name: 'Jordan Blake',
        worker_uuid: 'employee-jordan-blake',
        category: 'form',
    },
    {
        created_at: '2026-01-31T09:00:00Z',
        display_name: '1099-NEC — 2025',
        document_type: '1099_nec',
        document_uuid: 'document-contractor-1099',
        is_draft: false,
        kind: PayrollDocumentKindEnum.tax_form,
        owner_kind: OwnerKindEnum.contractor,
        requires_signature: false,
        signed_at: null,
        tax_quarter: null,
        tax_year: 2025,
        worker_name: 'Morgan Reyes',
        worker_uuid: 'contractor-morgan-reyes',
        category: 'form',
    },
    {
        created_at: '2026-08-01T09:00:00Z',
        display_name: 'W-2 — 2026',
        document_type: 'w2',
        document_uuid: 'document-draft-w2',
        is_draft: true,
        kind: PayrollDocumentKindEnum.tax_form,
        owner_kind: OwnerKindEnum.employee,
        requires_signature: false,
        signed_at: null,
        tax_quarter: null,
        tax_year: 2026,
        worker_name: 'Casey Nolan',
        worker_uuid: 'employee-casey-nolan',
        category: 'form',
    },
    {
        created_at: '2026-08-20T09:00:00Z',
        display_name: 'Form W-4',
        document_type: 'w4_federal',
        document_uuid: 'document-w4-outstanding',
        is_draft: false,
        kind: PayrollDocumentKindEnum.authorization,
        owner_kind: OwnerKindEnum.employee,
        requires_signature: true,
        signed_at: null,
        tax_quarter: null,
        tax_year: null,
        worker_name: 'Sam Okafor',
        worker_uuid: 'employee-sam-okafor',
        category: 'form',
    },
    {
        created_at: '2026-08-05T09:00:00Z',
        display_name: 'Direct Deposit Authorization',
        document_type: 'direct_deposit_authorization',
        document_uuid: 'document-w4-signed',
        is_draft: false,
        kind: PayrollDocumentKindEnum.authorization,
        owner_kind: OwnerKindEnum.employee,
        requires_signature: true,
        signed_at: '2026-08-06T14:00:00Z',
        tax_quarter: null,
        tax_year: null,
        worker_name: 'Riley Chen',
        worker_uuid: 'employee-riley-chen',
        category: 'form',
    },
    {
        created_at: '2026-06-10T09:00:00Z',
        display_name: 'Benefits Election Summary',
        // A slug this app has never defined — the no-client-filtering rule exists so this still
        // renders instead of disappearing the way the backend's own mis-tagging bug does today.
        document_type: 'benefits_election_summary',
        document_uuid: 'document-unrecognized',
        is_draft: false,
        kind: PayrollDocumentKindEnum.other,
        owner_kind: OwnerKindEnum.company,
        requires_signature: false,
        signed_at: null,
        tax_quarter: null,
        tax_year: null,
        worker_name: '',
        worker_uuid: null,
        category: 'form',
    },
    {
        created_at: '2026-08-25T09:00:00Z',
        display_name: 'W-2 — 2024',
        document_type: 'w2',
        document_uuid: 'document-employee-1-w2',
        is_draft: false,
        kind: PayrollDocumentKindEnum.tax_form,
        owner_kind: OwnerKindEnum.employee,
        requires_signature: false,
        signed_at: null,
        tax_quarter: null,
        tax_year: 2024,
        worker_name: 'Jamie Employee',
        worker_uuid: 'employee-1',
        category: 'form',
    },
    {
        created_at: '2026-01-31T09:00:00Z',
        display_name: '1099-NEC — 2024',
        document_type: '1099_nec',
        document_uuid: 'document-contractor-1-1099',
        is_draft: false,
        kind: PayrollDocumentKindEnum.tax_form,
        owner_kind: OwnerKindEnum.contractor,
        requires_signature: false,
        signed_at: null,
        tax_quarter: null,
        tax_year: 2024,
        worker_name: 'Jamie Contractor',
        worker_uuid: 'contractor-1',
        category: 'form',
    },
    {
        created_at: '2026-08-20T09:00:00Z',
        display_name: 'Form I-9',
        document_type: 'i9',
        document_uuid: 'document-employee-i9',
        is_draft: false,
        kind: PayrollDocumentKindEnum.identity_verification,
        owner_kind: OwnerKindEnum.employee,
        // Already completed, so it no longer asks for one: the provider flips this off once a form
        // is signed. The outstanding-signature row above is Sam's W-4, deliberately the only one.
        requires_signature: false,
        signed_at: null,
        tax_quarter: null,
        tax_year: null,
        worker_name: 'Sam Okafor',
        worker_uuid: 'employee-sam-okafor',
        category: 'form',
    },
    {
        created_at: '2026-08-31T09:00:00Z',
        display_name: 'Pay Stub — August 2026',
        document_type: 'pay_stub',
        document_uuid: 'document-employee-pay-stub',
        is_draft: false,
        kind: PayrollDocumentKindEnum.payroll_output,
        owner_kind: OwnerKindEnum.employee,
        requires_signature: false,
        signed_at: null,
        // A stub covers one run, not a filing period, so it has no tax period to show.
        tax_quarter: null,
        tax_year: null,
        worker_name: 'Jamie Employee',
        worker_uuid: 'employee-1',
        category: 'form',
    },
    {
        created_at: '2026-03-12T09:00:00Z',
        display_name: 'CA EDD Tax Rate Notice',
        document_type: 'tax_notice',
        document_uuid: 'document-company-tax-notice',
        is_draft: false,
        kind: PayrollDocumentKindEnum.agency_notice,
        owner_kind: OwnerKindEnum.company,
        requires_signature: false,
        signed_at: null,
        tax_quarter: null,
        tax_year: 2026,
        worker_name: '',
        worker_uuid: null,
        category: 'form',
    },
];

const GUSTO_COMPANY_ID = 'gusto-company-1';

/** The member's own employee row: the S-corp owner, whose forms the member signs themself. */
const OWNER_WORKER_UUID = 'employee-owner-alex-rivera';
const OWNER_NAME = 'Alex Rivera';

/** A company form, signed as the company's existing signatory. */
export const MOCK_QUEUE_COMPANY_FORM: PayrollSignatureQueueItem = {
    document_uuid: 'document-company-8655',
    category: 'form',
    owner_kind: 'company',
    worker_uuid: null,
    worker_name: '',
    display_name: 'Form 8655 — Reporting Agent Authorization',
    document_type: 'form_8655',
    kind: PayrollDocumentKindEnum.authorization,
    tax_year: null,
    tax_quarter: null,
    is_draft: false,
    signing: {
        component: 'company_signature_form',
        company_id: GUSTO_COMPANY_ID,
        form_id: 'gusto-form-8655',
        employee_id: null,
        signatory_id: 'gusto-signatory-1',
    },
};

/** The same company form for a company with no signatory yet, which signs through the assign step. */
export const MOCK_QUEUE_COMPANY_FORM_NO_SIGNATORY: PayrollSignatureQueueItem = {
    ...MOCK_QUEUE_COMPANY_FORM,
    signing: { ...MOCK_QUEUE_COMPANY_FORM.signing, signatory_id: null },
};

/**
 * The owner's two outstanding employee forms. Two on purpose: they are separate rows that share one
 * `Employee.DocumentSigner` flow, which is what the section copy explains.
 */
export const MOCK_QUEUE_OWNER_FORMS: PayrollSignatureQueueItem[] = [
    {
        document_uuid: 'document-owner-w4',
        category: 'form',
        owner_kind: 'employee',
        worker_uuid: OWNER_WORKER_UUID,
        worker_name: OWNER_NAME,
        display_name: 'Form W-4',
        document_type: 'w4_federal',
        kind: PayrollDocumentKindEnum.authorization,
        tax_year: null,
        tax_quarter: null,
        is_draft: false,
        signing: {
            component: 'employee_document_signer',
            company_id: GUSTO_COMPANY_ID,
            form_id: 'gusto-form-owner-w4',
            employee_id: 'gusto-employee-owner',
            signatory_id: null,
        },
    },
    {
        document_uuid: 'document-owner-direct-deposit',
        category: 'form',
        owner_kind: 'employee',
        worker_uuid: OWNER_WORKER_UUID,
        worker_name: OWNER_NAME,
        display_name: 'Direct Deposit Authorization',
        document_type: 'direct_deposit_authorization',
        kind: PayrollDocumentKindEnum.authorization,
        tax_year: null,
        tax_quarter: null,
        is_draft: false,
        signing: {
            component: 'employee_document_signer',
            company_id: GUSTO_COMPANY_ID,
            form_id: 'gusto-form-owner-direct-deposit',
            employee_id: 'gusto-employee-owner',
            signatory_id: null,
        },
    },
];

export const MOCK_SIGNATURE_QUEUE: PayrollSignatureQueue = {
    count: 3,
    results: [MOCK_QUEUE_COMPANY_FORM, ...MOCK_QUEUE_OWNER_FORMS],
};

/** A queue item as the library lists it: the same document, `needs_your_signature`. */
const libraryRowFor = (item: PayrollSignatureQueueItem): PayrollLibraryDocument => ({
    category: item.category,
    created_at: '2026-09-01T09:00:00Z',
    display_name: item.display_name,
    document_type: item.document_type,
    document_uuid: item.document_uuid,
    is_draft: item.is_draft,
    kind: item.kind,
    owner_kind: item.owner_kind,
    requires_signature: true,
    signature_status: 'needs_your_signature',
    signed_at: null,
    tax_quarter: item.tax_quarter,
    tax_year: item.tax_year,
    worker_name: item.worker_name,
    worker_uuid: item.worker_uuid,
});

/**
 * One row per Signature outcome the page draws: "Signed" with a date (a contractor's W-9, the one
 * document the provider dates), "Awaiting {name}" (another worker's outstanding form), and "Not
 * required" (signed or never needed a signature — the provider doesn't say which). The queue's documents are
 * in here too, as the library returns them, so the page's de-duplication has something to remove.
 */
export const MOCK_LIBRARY_DOCUMENTS: PayrollLibraryDocument[] = [
    ...MOCK_SIGNATURE_QUEUE.results.map(libraryRowFor),
    {
        category: 'form',
        created_at: '2026-07-15T12:00:00Z',
        display_name: 'Form 941 — Q2 2026',
        document_type: 'form_941',
        document_uuid: 'document-company-941',
        is_draft: false,
        kind: PayrollDocumentKindEnum.tax_form,
        owner_kind: OwnerKindEnum.company,
        requires_signature: false,
        signature_status: 'none',
        signed_at: null,
        tax_quarter: 2,
        tax_year: 2026,
        worker_name: '',
        worker_uuid: null,
    },
    {
        category: 'form',
        created_at: '2026-01-31T09:00:00Z',
        display_name: 'W-2 — 2025',
        document_type: 'w2',
        document_uuid: 'document-employee-w2',
        is_draft: false,
        kind: PayrollDocumentKindEnum.tax_form,
        owner_kind: OwnerKindEnum.employee,
        requires_signature: false,
        signature_status: 'none',
        signed_at: null,
        tax_quarter: null,
        tax_year: 2025,
        worker_name: 'Jordan Blake',
        worker_uuid: 'employee-jordan-blake',
    },
    {
        category: 'form',
        created_at: '2026-08-20T09:00:00Z',
        display_name: 'Form W-4',
        document_type: 'w4_federal',
        document_uuid: 'document-employee-w4-awaiting',
        is_draft: false,
        kind: PayrollDocumentKindEnum.authorization,
        owner_kind: OwnerKindEnum.employee,
        requires_signature: true,
        signature_status: 'awaiting_signature',
        signed_at: null,
        tax_quarter: null,
        tax_year: null,
        worker_name: 'Sam Okafor',
        worker_uuid: 'employee-sam-okafor',
    },
    {
        category: 'document',
        created_at: '2026-03-02T09:00:00Z',
        display_name: 'Form W-9',
        document_type: 'w9',
        document_uuid: 'document-contractor-w9',
        is_draft: false,
        kind: PayrollDocumentKindEnum.tax_form,
        owner_kind: OwnerKindEnum.contractor,
        requires_signature: false,
        signature_status: 'signed',
        signed_at: '2026-03-04T15:30:00Z',
        tax_quarter: null,
        tax_year: null,
        worker_name: 'Morgan Reyes',
        worker_uuid: 'contractor-morgan-reyes',
    },
    {
        category: 'form',
        created_at: '2026-01-31T09:00:00Z',
        display_name: '1099-NEC — 2025',
        document_type: '1099_nec',
        document_uuid: 'document-contractor-1099',
        is_draft: false,
        kind: PayrollDocumentKindEnum.tax_form,
        owner_kind: OwnerKindEnum.contractor,
        requires_signature: false,
        signature_status: 'none',
        signed_at: null,
        tax_quarter: null,
        tax_year: 2025,
        worker_name: 'Morgan Reyes',
        worker_uuid: 'contractor-morgan-reyes',
    },
    {
        category: 'form',
        created_at: '2026-08-01T09:00:00Z',
        display_name: 'W-2 — 2026',
        document_type: 'w2',
        document_uuid: 'document-draft-w2',
        is_draft: true,
        kind: PayrollDocumentKindEnum.tax_form,
        owner_kind: OwnerKindEnum.employee,
        requires_signature: false,
        signature_status: 'none',
        signed_at: null,
        tax_quarter: null,
        tax_year: 2026,
        worker_name: 'Casey Nolan',
        worker_uuid: 'employee-casey-nolan',
    },
];

export const MOCK_LIBRARY: PayrollDocumentLibrary = {
    count: MOCK_LIBRARY_DOCUMENTS.length,
    results: MOCK_LIBRARY_DOCUMENTS,
    incomplete: false,
    unavailable_owners: [],
};

/** Two workers' reads failed or were skipped: their rows are missing, the rest still render. */
export const MOCK_INCOMPLETE_LIBRARY: PayrollDocumentLibrary = {
    ...MOCK_LIBRARY,
    incomplete: true,
    unavailable_owners: [
        {
            owner_kind: OwnerKindEnum.employee,
            worker_uuid: 'employee-taylor-kim',
            worker_name: 'Taylor Kim',
            reason: PayrollDocumentUnavailableReasonEnum.unavailable,
        },
        {
            owner_kind: OwnerKindEnum.contractor,
            worker_uuid: 'contractor-drew-park',
            worker_name: 'Drew Park',
            reason: PayrollDocumentUnavailableReasonEnum.unavailable,
        },
    ],
};

/** Gusto has no record of the member's own employee, so a retry won't bring their documents back. */
export const MOCK_OWNER_NOT_FOUND_LIBRARY: PayrollDocumentLibrary = {
    ...MOCK_LIBRARY,
    incomplete: true,
    unavailable_owners: [
        {
            owner_kind: OwnerKindEnum.employee,
            worker_uuid: 'employee-jamie-ortiz',
            worker_name: 'Jamie Ortiz',
            reason: PayrollDocumentUnavailableReasonEnum.not_found,
        },
    ],
};
